import React, { useState, useEffect } from 'react'
import WithdrawModal from './WithdrawModal'

const Balance = () => {
  const [userData, setUserData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [withdrawMessage, setWithdrawMessage] = useState(null)

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        // Get token from localStorage
        const token = localStorage.getItem('token');
        
        if (!token) {
          throw new Error('No authentication token found. Please login again.');
        }

        const myHeaders = new Headers()
        myHeaders.append("Accept", "application/json")
        myHeaders.append("Authorization", `Bearer ${token}`)

        const requestOptions = {
          method: "GET",
          headers: myHeaders,
          redirect: "follow"
        }

        const response = await fetch("https://devapi.quickcare.ng/api/v1/user/profile", requestOptions)
        
        if (!response.ok) {
          // Handle unauthorized error (token expired or invalid)
          if (response.status === 401) {
            localStorage.removeItem('token');
            throw new Error('Session expired. Please login again.');
          }
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()
        
        if (result.success) {
          setUserData(result.data)
        } else {
          throw new Error(result.message || 'Failed to fetch user data')
        }
      } catch (error) {
        console.error('Error fetching user profile:', error)
        setError(error.message)
      } finally {
        setLoading(false)
      }
    }

    fetchUserProfile()
  }, [])

  const openModal = () => {
    // Check if user has sufficient balance before opening modal
    if (userData && parseFloat(userData.wallet_balance) > 0) {
      setIsModalOpen(true)
    } else {
      alert('Insufficient balance for withdrawal')
    }
  }

  const closeModal = () => setIsModalOpen(false)

  const handleWithdrawSuccess = (message) => {
    setWithdrawMessage(message);
    
    // Set timeout to reload the page after 2 seconds
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-40">
        <div className="text-gray-600">Loading balance information...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-40">
        <div className="text-red-600 bg-red-100 p-4 rounded-lg">
          Error: {error}
        </div>
      </div>
    )
  }

  if (!userData) {
    return (
      <div className="flex justify-center items-center min-h-40">
        <div className="text-gray-600">No data available</div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Withdrawal Success Message */}
      {withdrawMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          <p className="font-medium">{withdrawMessage}</p>
          <p className="text-sm">Page will reload in 2 seconds...</p>
        </div>
      )}
      
      {/* Header with Balance Title and Withdraw Button */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Account Balance</h1>
        <button
          onClick={openModal}
          disabled={parseFloat(userData.wallet_balance || 0) <= 0}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-200"
        >
          Withdraw
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Wallet Balance Card */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-700">Wallet Balance</h2>
              <p className="text-3xl font-bold text-green-600 mt-2">
                ₦{parseFloat(userData.wallet_balance || 0).toLocaleString()}
              </p>
              <p className="text-sm text-gray-500 mt-2">Available for use</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Pending Withdrawal Card */}
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-700">Pending Withdrawal</h2>
              <p className="text-3xl font-bold text-blue-600 mt-2">
                ₦{parseFloat(userData.pending_withdrawal_amount || 0).toLocaleString()}
              </p>
              <p className="text-sm text-gray-500 mt-2">Awaiting processing</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* User Info Section */}
      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Account Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600">Name</p>
            <p className="font-medium text-gray-800">
              {userData.first_name} {userData.last_name}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Phone Number</p>
            <p className="font-medium text-gray-800">{userData.phone_number}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Email</p>
            <p className="font-medium text-gray-800">{userData.email}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Status</p>
            <p className="font-medium text-gray-800">
              {userData.is_subscribed ? 'Subscribed' : 'Not Subscribed'}
            </p>
          </div>
        </div>
      </div>

      {/* Withdraw Modal */}
      <WithdrawModal 
        isOpen={isModalOpen}
        onClose={closeModal}
        walletBalance={userData.wallet_balance}
        userData={userData}
        onWithdrawSuccess={handleWithdrawSuccess}
      />
    </div>
  )
}

export default Balance